import SheetMap from '../Minimap3D/SheetMap.js';
import { compute3DTo2DMatrix, setTopView, posIsInsideMap }  from '../Minimap3D/MinimapMath.js';
import { SheetSyncEvents } from './SheetSyncConstants.js';
import { SHOW_SHEET_EVENT, HIDE_SHEET_EVENT } from '../../ModelSheetTransition/EventTypes.js';
import SheetVisibilityManager from './SheetVisibilityManager.js';

const av = Autodesk.Viewing;
const avp = av.Private;
const Empty2DModelLoader = avp.Empty2DModelLoader;

const namespace = AutodeskNamespace('Autodesk.AEC');
const myExtensionName = 'Autodesk.AEC.SheetSyncExtension';

/**
// SheetSyncExtension - A viewer extension that syncs between a 3D model and its corresponding 2D sheet.
//                      Works only when BimWalk is active. Whenever the player changes position, the 2D sheet will be updated according to the correct viewport.
//
// @param {Viewer3D} [viewer]                       - viewer instance
// @param {Object} [options]                        - Configuration options for SheetSyncExtension
// @param {Viewer3D} [options.targetViewer]         - Target viewer instance.
// @param {bool} [options.cropToViewport]           - When enabled, the viewport that's related to the 3D camera will be cropped.
// @param {bool} [options.hideSheetWhenNotFocused]  - When enabled, when the 3D camera is not inside the sheet's viewport, the sheet will disappear.
// @param {function} [options.autoSwitchSheet]      - Automatically load sheet when map has been changed.
// @param {function} [options.loadDocumentNode]     - Optional item loading API.
// @param {function} [options.unloadModel]          - Optional item unloading loading API.
// @param {Viewer3D} [options.hidePaper]            - Hide the white paper from the 2D sheet.
// @param {Viewer3D} [options.useCustomModel]       - Use a specific 3D model to sync, instead of using all visible models by default.
*/
export default class SheetSyncExtension extends Autodesk.Viewing.Extension {
    constructor(viewer, options = {}) {
        super(viewer, options);

        this.options = this.options.sheetSyncOptions || this.options;
        this.targetViewer = this.options.targetViewer;

        // SheetMap that is relevant to the current camera position.
        this.sheetMap = null;

        // Models in 3D target viewer that we are going to sync to.
        this.targetModels = [];

        // Cache for all sheetMapConfigs.
        this.mapCache = {};

         // Cache for fuzzyBox per model id.
        this.modelIdToFuzzyBox = [];

        // [modelKey -> boolean] map that represents if a sheet model is being currently loaded.
        this.isModelLoadingMap = {};

         // Whether to show 2D sheet. Show sheet by default.
        const showSheet = this.options.showSheet !== undefined ? this.options.showSheet : true;
        this.setShowSheet(showSheet);

        // Controller of 2D sheet visibility and crop.
        this.sheetVisibilityManager = new SheetVisibilityManager(this.viewer, this.options);

        this.onNavigationToolChanged = this.onNavigationToolChanged.bind(this);
        this.onSheetGeometryLoaded = this.onSheetGeometryLoaded.bind(this);
        this.onModelAdded = this.onModelAdded.bind(this);
        this.onModelRemoved = this.onModelRemoved.bind(this);
        this.on3dGeometryLoaded = this.on3dGeometryLoaded.bind(this);
        this.updateSheetAppearance = this.updateSheetAppearance.bind(this);
        this.isCameraInsideMap = this.isCameraInsideMap.bind(this);
        this.onModelTransformChanged = this.onModelTransformChanged.bind(this);
        this.update = this.update.bind(this);
        this.onViewportDataFetched = this.onViewportDataFetched.bind(this);
        this.onModelSheetShow = this.onModelSheetShow.bind(this);
        this.onModelSheetHide = this.onModelSheetHide.bind(this);
    }

    async load() {
        if (!this.targetViewer) {
            return false;
        }

        this.viewportsExtension = await this.targetViewer.loadExtension('Autodesk.AEC.ViewportsExtension');

        this.targetViewer.addEventListener(av.NAVIGATION_MODE_CHANGED_EVENT, this.onNavigationToolChanged);

        this.onNavigationToolChanged();

        return true;
    }

    unload() {
        this.deactivate();

        this.viewportsExtension = null;

        // Clear cut planes if needed, and update opacity.
        this.sheetVisibilityManager.setVisibilityImmediate(true);
        this.sheetVisibilityManager.updateCrop(null);

        // Restore paper for all sheets.
        this.changePaperVisibility(true);

        this.sheetMap = null;
        this.targetModels = [];

        this.targetViewer.removeEventListener(av.NAVIGATION_MODE_CHANGED_EVENT, this.onNavigationToolChanged); // This listener should be removed specifically here, not inside removeListeners.

        return true;
    }

    onViewportDataFetched() {
        this.invalidateSheetMaps();
    }

    changePaperVisibility(show, models) {
        // Nothing to change in case hidePaper was never set.
        if (!this.options.hidePaper) {
            return;
        }

        models = models || this.viewer.getAllModels();

        models.forEach(model => {
            model.changePaperVisibility(show);
        });

        this.viewer.impl.invalidate(true);
    }

    activate() {
        if (this.active) {
            return;
        }

        this.active = true;

        this.addListeners();

        this.on3dGeometryLoaded();

        // Make sure all papers are hidden if needed.
        this.changePaperVisibility(false);
    }

    deactivate() {
        if (!this.active) {
            return;
        }

        this.active = false;

        this.sheetMap = null;

        this.removeListeners();
    }

    addListeners() {
        this.targetViewer.addEventListener(av.MODEL_ADDED_EVENT, this.onModelAdded);
        this.targetViewer.addEventListener(av.MODEL_REMOVED_EVENT, this.onModelRemoved);
        this.targetViewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.update);
        this.targetViewer.addEventListener(Autodesk.Viewing.MODEL_TRANSFORM_CHANGED_EVENT, this.onModelTransformChanged);
        this.targetViewer.addEventListener(av.GEOMETRY_LOADED_EVENT, this.on3dGeometryLoaded);
        this.targetViewer.addEventListener(SHOW_SHEET_EVENT, this.onModelSheetShow);
        this.targetViewer.addEventListener(HIDE_SHEET_EVENT, this.onModelSheetHide);

        for (const model of this.targetViewer.getAllModels()) {
            // Event listeners added here should also be added in onModelAdded.
            model.addEventListener(av.MODEL_FRAGMENT_BOUNDING_BOXES_SET_EVENT, this.on3dGeometryLoaded);
        }
        this.targetViewer.addEventListener(Autodesk.AEC.LevelsExtension.LEVEL_CHANGED, this.updateSheetAppearance);
        this.viewer.addEventListener(av.MODEL_ROOT_LOADED_EVENT, this.onSheetGeometryLoaded);
        this.viewer.addEventListener(av.GEOMETRY_LOADED_EVENT, this.onSheetGeometryLoaded);
        this.viewer.addEventListener(av.TEXTURES_LOADED_EVENT, this.onSheetGeometryLoaded);
        this.viewer.addEventListener(SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, this.updateSheetAppearance);


        this.viewportsExtension.addEventListener(Autodesk.AEC.ViewportsExtension.Events.VIEWPORT_DATA_FETCHED_EVENT, this.onViewportDataFetched);

        const levelExt = this.targetViewer.getExtension('Autodesk.AEC.LevelsExtension');
        levelExt?.floorSelector?.addEventListener(Autodesk.AEC.FloorSelector.SELECTED_FLOOR_CHANGED, this.update);
        levelExt?.floorSelector?.addEventListener(Autodesk.AEC.FloorSelector.FLOOR_DATA_CHANGED, this.onViewportDataFetched);

    }

    removeListeners() {
        this.targetViewer.removeEventListener(av.MODEL_ADDED_EVENT, this.onModelAdded);
        this.targetViewer.removeEventListener(av.MODEL_REMOVED_EVENT, this.onModelRemoved);
        this.targetViewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.update);
        this.targetViewer.removeEventListener(Autodesk.Viewing.MODEL_TRANSFORM_CHANGED_EVENT, this.onModelTransformChanged);
        this.targetViewer.removeEventListener(av.GEOMETRY_LOADED_EVENT, this.on3dGeometryLoaded);
        this.targetViewer.removeEventListener(SHOW_SHEET_EVENT, this.onModelSheetShow);
        this.targetViewer.removeEventListener(HIDE_SHEET_EVENT, this.onModelSheetHide);

        for (const model of this.targetViewer.getAllModels()) {
            // Event listeners removed here should also be removed in onModelRemoved.
            model.removeEventListener(av.MODEL_FRAGMENT_BOUNDING_BOXES_SET_EVENT, this.on3dGeometryLoaded);
        }
        this.targetViewer.removeEventListener(Autodesk.AEC.LevelsExtension.LEVEL_CHANGED, this.updateSheetAppearance);
        this.viewer.removeEventListener(av.MODEL_ROOT_LOADED_EVENT, this.onSheetGeometryLoaded);
        this.viewer.removeEventListener(av.GEOMETRY_LOADED_EVENT, this.onSheetGeometryLoaded);
        this.viewer.removeEventListener(av.TEXTURES_LOADED_EVENT, this.onSheetGeometryLoaded);
        this.viewer.removeEventListener(SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, this.updateSheetAppearance);

        this.viewportsExtension.removeEventListener(Autodesk.AEC.ViewportsExtension.Events.VIEWPORT_DATA_FETCHED_EVENT, this.onViewportDataFetched);

        const levelExt = this.targetViewer.getExtension('Autodesk.AEC.LevelsExtension');
        levelExt?.floorSelector?.removeEventListener(Autodesk.AEC.FloorSelector.SELECTED_FLOOR_CHANGED, this.update);
        levelExt?.floorSelector?.removeEventListener(Autodesk.AEC.FloorSelector.FLOOR_DATA_CHANGED, this.onViewportDataFetched);
    }

    isBimWalkActive() {
        return this.targetViewer.getActiveNavigationTool() === 'bimwalk';
    }

    isEnabled() {
        return this.isBimWalkActive() || this.targetViewer.getExtension('Autodesk.AEC.Minimap3DExtension')?.isEnabled();
    }

    onModelSheetShow({ model }) {
        this.modelSheetVisible = true;
    }

    onModelSheetHide({ model }) {
        this.modelSheetVisible = false;
    }

    onNavigationToolChanged() {
        if (this.isEnabled()) {
            this.activate();
        } else {
            this.deactivate();
        }

        this.update();
    }

    // Update sheet's opacity and crop bounds.
    updateSheetAppearance() {
        if (!this.isSheetMapLoaded(this.sheetMap) || !this.isMapOnCurrentLevel(this.sheetMap)) {
            this.sheetVisibilityManager.setVisibilityImmediate(false);
            this.sheetVisibilityManager.updateCrop(null);
        } else {
            let visible = this.showSheet && this.isCameraInsideMap(this.sheetMap) && this.isEnabled();
            // VIZX-4507:
            // If there is a visible sheet in the target viewer, we keep the sheet visibility in the minimap in sync with the 3D view.
            // Simultaneously, when in first person mode and there is no sheet in the target viewer, we want to skip the sync, so we still see the sheet of the level we are on.
            if (this.modelSheetVisible || !this.isBimWalkActive()) {
                visible &&= this.isSheetVisibleInTargetViewer(this.sheetMap);
            }
            this.sheetVisibilityManager.updateSheetAppearance(this.sheetMap, visible);
        }
    }

    isSheetVisibleInTargetViewer(sheetMap) {
        const model = sheetMap.model;
        if (!model) {
            return false;
        }

        const guid = model.getDocumentNode().guid();
        const visible = this.targetViewer.getVisibleModels().some(m => m.getDocumentNode().guid() === guid);

        return visible;
    }

    // Get a union of all targetModel's fuzzy-boxes.
    getFuzzyBox() {
        const fuzzyBoxRes = new THREE.Box3();

        // sum up fuzzy boxes of all map models
        for (let i=0; i<this.targetModels.length; i++) {

            const model = this.targetModels[i];
            if (model && model.loader instanceof Autodesk.Viewing.Private.OtgLoader) {
                if (!model.loader.fragmentDataLoaded) {
                    continue;
                }
            }

            // get bbox from cache if possible
            let bbox = this.modelIdToFuzzyBox[model.id];

            if (!bbox) {
                // compute fuzzybox and add it to cache
                bbox = model.getFuzzyBox();

                let loadDone = model.isLoadDone() || model.getFragmentList().fragments.boxesLoaded;

                // If we are loading an Otg model, we check that OTG has loaded all fragment data and
                // that the fragment list in the model has been updated to have the same number of fragments.
                if (model && model.loader instanceof Autodesk.Viewing.Private.OtgLoader) {
                    if (model.loader.fragmentDataLoaded &&
                         model.getFragmentList().fragments.length === model.loader.svf.fragments.length) {
                            loadDone = true;
                    }
                }

                if (loadDone) {
                    this.modelIdToFuzzyBox[model.id] = bbox;
                }
            }

            fuzzyBoxRes.union(bbox);
        }

        return fuzzyBoxRes;
    }

    // Generate a dummy sheetMap, according to the entire 3D model bounding box.
    // Initially, the map should fully fit the canvas.
    generateDummyMap() {
        // If sheetmap already set, no need to generate dummy map.
        if (this.sheetMap) {
            return;
        }

        const levelExt = this.targetViewer.getExtension('Autodesk.AEC.LevelsExtension');
        const currentLevel = levelExt && levelExt.getCurrentLevel();
        const zRange = currentLevel && levelExt.getZRange(currentLevel.index);

        const modelFuzzyBox = this.getFuzzyBox();

        if (modelFuzzyBox.isEmpty()) {
            return;
        }

        // If no level zRange is specified, use the zRange enclosing all visible models
        const zMin = zRange ? zRange.zMin : modelFuzzyBox.min.z;
        const zMax = zRange ? zRange.zMax : modelFuzzyBox.max.z;

        modelFuzzyBox.min.z = 0;
        modelFuzzyBox.max.z = 0;

        const renderer = this.viewer.impl.renderer();
        const canvasWidth = renderer.settings.logicalWidth;
        const canvasHeight = renderer.settings.logicalHeight;

        const camera = new av.Camera(canvasWidth, canvasHeight);
        setTopView(camera, modelFuzzyBox, zMin, zMax, modelFuzzyBox.getSize(new THREE.Vector3()).y);

        const mapMatrix = new THREE.Matrix4();
        compute3DTo2DMatrix(camera, mapMatrix);
        const mapMatrixInv = mapMatrix.clone().invert();

        const min = this.viewer.clientToWorld(0, 0, undefined, true).point;
        const max = this.viewer.clientToWorld(canvasWidth, canvasHeight, undefined, true).point;

        const viewportBounds = new THREE.Box3().setFromPoints([min, max]);

        this.sheetMap = {
            viewportBounds,
            mapMatrix,
            mapMatrixInv,
            viewport: {
                getGuid: () => 'dummyViewportGuid',
                getSheetName: () => 'dummySheetName',
                getSheetUrn: () => 'dummySheetUrn',
            }
        };

        this.viewer.dispatchEvent({ type: SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, sheetMap: this.sheetMap });

        this.update();
    }

    /**
     * @param {Object} event
     * @param {Model} event.model
     */
    onModelAdded({ model }) {
        if (model.is3d()) {
            model.addEventListener(av.MODEL_FRAGMENT_BOUNDING_BOXES_SET_EVENT, this.on3dGeometryLoaded);
        }

        this.update();
    }

    /**
     * @param {Object} event
     * @param {Model} event.model
     */
    onModelRemoved({ model }) {
        if (model.is3d()) {
            model.removeEventListener(av.MODEL_FRAGMENT_BOUNDING_BOXES_SET_EVENT, this.on3dGeometryLoaded);
        }
        else {
            // Remove the sheet from the viewer, so it won't show up alongside other sheets.
            const removedGuid = model.getDocumentNode().guid();
            this.viewer.getAllModels().forEach(model => {
                if (model.getDocumentNode().guid() === removedGuid) {
                    this.viewer.impl.removeModel(model);
                }
            });
        }

        this.update();
    }

    on3dGeometryLoaded() {
        this.updateTargetModels();

        // No target models yet.
        if (!this.targetModels) {
            return;
        }

        this.generateDummyMap();

        this.update();
    }

    // Hide sheet's paper.
    onSheetGeometryLoaded({ model }) {
        this.changePaperVisibility(false, [model]);
        this.update();
    }

    invalidateSheetMaps() {
        Object.keys(this.mapCache).forEach(guid => {
            // Create a new mapMatrix according to the transformed models.
            const newMaps = SheetMap.createSheetMapConfigs(this.targetModels, guid, this.viewportsExtension, this.options.useOnlyAecModelDataViewports);

            newMaps.forEach(newMap => {
                const map = this.mapCache[guid].find(m => m.viewport.getGuid() === newMap.viewport.getGuid());

                // It's important to keep the original maps, and change only the matrix,
                // since there is additional attributes that already assigned to the map instances(i.e.thumbnail).
                if (map) {
                    map.mapMatrix = newMap.mapMatrix;
                    map.mapMatrixInv = newMap.mapMatrixInv;

                    // Mark the thumbnail mesh as dirty, so in ThumbnailOverlay it will be repositioned.
                    if (map.thumbnail?.mesh) {
                        map.thumbnail.mesh.dirty = true;
                    }
                } else {
                    // New map detected.
                    // Calculate viewport bounds and store it as part of the sheetMap.
                    newMap.viewportBounds = newMap.viewport.getViewportBounds(this.viewer.model.getUnitScale());
                    this.mapCache[guid].push(newMap);
                }
            });
        });

        this.update();

        // This is needed in order to sync the camera of the TopViewRenderer, as it was a new sheetMap.
        this.viewer.dispatchEvent({ type: SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, sheetMap: this.sheetMap });
    }

    onModelTransformChanged({ model }) {
        // If model is not part of targetModels - skip.
        if (!this.targetModels.includes(model)) {
            return;
        }

        // Clear fuzzyBox cache for the transformed model, so it will get calculated again.
        this.modelIdToFuzzyBox[model.id] = null;

        this.invalidateSheetMaps();
    }

    updateTargetModels() {
        if (!this.options.useCustomModel) {
            // Filter models
            const isTargetModel = (m) => {
                // Only 3D models.
                const is3d = m.is3d();
                // The current implementation can only deal with models that have a document node.
                // Other models - e.g. from the ModelBuilder extension - won't work.
                const hasDocumentNode = !!m.getDocumentNode();

                return is3d && hasDocumentNode;
            };
            const models = this.targetViewer.getVisibleModels().filter(isTargetModel);

            this.setTargetModels(models);
        }
    }

    setTargetModels(models) {
        this.targetModels = models;
    }

    getSheetNodeKey(map) {
        const key = map && `${map.viewport.getSheetUrn()}_${map.viewport.getSheetName()}`;
        return key;
    }

    isLoadingSheetNode(map) {
        const key = this.getSheetNodeKey(map);
        return key && this.isModelLoadingMap[key];
    }

    shouldSwitchSheet() {
        const showSheet = this.showSheet;
        const isAutoSwitchEnabled = this.options.autoSwitchSheet;
        const isDifferentSheetLoaded = !this.isSheetMapLoaded(this.sheetMap);
        const isCameraInside = this.isCameraInsideMap(this.sheetMap);
        const isAlreadyLoading = this.isLoadingSheetNode(this.sheetMap);
        const isMinimapDebugMode = this.options.minimapDebugMode;

        return isAutoSwitchEnabled && isDifferentSheetLoaded && showSheet && isCameraInside && !isAlreadyLoading && !isMinimapDebugMode;
    }

    getCurrentLevel() {
        const levelExt = this.targetViewer.getExtension('Autodesk.AEC.LevelsExtension');

        if (!levelExt) {
            return;
        }

        return levelExt.getCurrentLevel();
    }

    update() {
        if (!this.viewer.model) {
            return;
        }

        const level = this.getCurrentLevel();

        if (!level) {
            return;
        }

        this.updateTargetModels();

        if (!this.isEnabled()) {
            return;
        }

        this.updateSheetMaps();

        this.updateSheetAppearance();

        this.switchSheetIfNeededDebounce();
    }

    switchSheetIfNeededDebounce() {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }

        this.debounceTimeout = setTimeout(this.switchSheetIfNeeded.bind(this), 300);
    }

    // Return a custom hash that encapsulates the current sheet map viewer id.
    getCustomHash() {
        return `sheetsync_${this.viewer.id}`;
    }

    async switchSheetIfNeeded() {
        if (this.shouldSwitchSheet()) {
            this.viewer.dispatchEvent({ type: SheetSyncEvents.CHANGING_SHEET_EVENT });

            // Load sheet
            const previousModel = this.viewer.model;
            const sheetMap = this.sheetMap;
            const sheetNode = await this.sheetMap.getSheetNode();

            // Verify that sheetMap wasn't changed until we got back from the async function.
            if (sheetMap !== this.sheetMap) {
                return;
            }

            const doc = sheetNode.getRootNode().getDocument();
            const initialOpacity = this.options.hideSheetWhenNotFocused ? 0.0 : 1.0;
            const loadFunction = this.options.loadDocumentNode || this.viewer.loadDocumentNode.bind(this.viewer);
            const unloadFunction = this.options.unloadModel || this.viewer.unloadModel.bind(this.viewer);
            const key = this.getSheetNodeKey(sheetMap);
            this.isModelLoadingMap[key] = true;
            // There are some critical flags here:
            // loadAsHidden - We have to first load the model is hidden, and only then show it. The reason is because we
            //                have to make sure that `renderer.enter2DMode()` will be called, and it won't get called if there is more the one loaded model.
            //                Without calling `enter2DMode`, there is a chance for different rendering errors.
            //
            // keepCurrentModels - In order to prevent tearDown of the viewer, a thing that will cause the navigation to stop,
            //                     we have to first load the new model (as hidden), then hide the previous model, and then show the new one.
            // preserveView - Keeps the camera in the same spot.
            loadFunction(doc, sheetNode, { opacity: initialOpacity, preserveView: true, keepCurrentModels: true, loadAsHidden: true, customHash: this.getCustomHash() })
                .catch((error) => console.error("Failed to load sheet: ", sheetNode.name(), ": ", error))
                .then((model) => {
                    if (previousModel && previousModel !== model) {
                        // In case the previous model was the initial empty model - just unload it. There is no reason to support caching it.
                        if (previousModel.loader instanceof Empty2DModelLoader) {
                            this.viewer.unloadModel(previousModel);
                        } else {
                            unloadFunction(previousModel);
                        }
                    }

                    this.isModelLoadingMap[key] = false;
                    this.viewer.showModel(model, true);

                    // In case we loaded a leaflet, we don't want to restrict zoom in/out when viewing in the minimap.
                    // Therefore, we revert the constraints that are being set up in viewer.initializeFirstModelPresets.
                    this.viewer.navigation.setConstraints2D();

                    this.sheetVisibilityManager.setVisibilityImmediate(false);

                    // Update viewportBounds with model unit scale.
                    sheetMap.viewportBounds = sheetMap.viewport.getViewportBounds(model.getUnitScale());
                    sheetMap.model = model;

                    this.update();
                    this.viewer.dispatchEvent({ type: SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, sheetMap });
                });
        }
    }

    // we can't just shallow compare the instances, because SheetMap.createSheetMapConfigs returns a new pointer each time.
    // Instead, we compare the viewportGuid, that stays the same.
    isSheetMapsEqual(map1, map2) {
        return map1?.viewport.getGuid() === map2?.viewport.getGuid();
    }

    isMapOnCurrentLevel(map) {
        if (!map) {
            return false;
        }

        const currentLevel  = this.getCurrentLevel();

        // Different level.
        if (!currentLevel || map.levelGuid !== currentLevel.guid) {
            return false;
        }

        return true;
    }

    isSheetMapLoaded(map) {
        return map?.model === this.viewer.model;
    }

    // Is current 3D camera is inside a loaded map.
    isCameraInsideMap(map) {
        if (!map) {
            return false;
        }

        if (!this.isMapOnCurrentLevel(map)) {
            return false;
        }

        // Relevant sheet is not currently loaded.
        if (!this.options.autoSwitchSheet && !this.isSheetMapLoaded(map)) {
            return false;
        }

        return posIsInsideMap(this.targetViewer.getCamera().position, map.mapMatrix);
    }

    // Update current sheet map according to current level.
    updateSheetMaps() {
        let maps;

        const currentLevel  = this.getCurrentLevel();

        if (!this.mapCache[currentLevel.guid]) {
            // Find maps that are relevant to these models, and for the current level.
            maps = SheetMap.createSheetMapConfigs(this.targetModels, currentLevel.guid, this.viewportsExtension, this.options.useOnlyAecModelDataViewports);

            const currentModel = this.viewer.model;
            const currentBubbleNode = currentModel.getDocumentNode();

            maps.forEach(map => {
                // Calculate viewport bounds and store it as part of the sheetMap.
                map.viewportBounds = map.viewport.getViewportBounds(currentModel.getUnitScale());

                // If the current loaded model is the map's model.
                if (map.viewport.getSheetName() === currentBubbleNode.name() && map.viewport.getSheetUrn() === currentBubbleNode.urn(true)) {
                    map.model = currentModel;
                }
            });

            this.mapCache[currentLevel.guid] = maps;
        } else {
            maps = this.mapCache[currentLevel.guid];
        }

         // In case we didn't find another map - keep it only if cropToViewport.
         // Otherwise, it can cause a confusion when the avatar is floating above a viewport that it doesn't really belongs to.
         // In that case, unset the map.
        const map = maps.find(this.isCameraInsideMap) || (this.options.cropToViewport ? this.sheetMap : undefined);

        const mapChanged = !this.isSheetMapsEqual(this.sheetMap, map);

        this.sheetMap = map;

        if (mapChanged) {
            this.viewer.dispatchEvent({ type: SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, sheetMap: this.sheetMap });
        }
    }

    setShowSheet(show) {
        this.showSheet = show;

        if (this.active) {
            this.update();
        }
    }

    getShowSheet() {
        return this.showSheet;
    }

    isSheetVisible() {
        return (
            this.showSheet &&
            this.isSheetMapLoaded(this.sheetMap) &&
            this.sheetVisibilityManager.isSheetVisible() &&
            this.viewer.model.isLoadDone()
        );
    }

    expandCropBounds(duration) {
        this.sheetVisibilityManager.expandCropBounds(this.sheetMap, duration);
    }

    setSheetOpaque(duration) {
        this.sheetVisibilityManager.setSheetOpaque(duration);
    }
}
Object.assign(namespace, { SheetSyncEvents });
namespace.SheetSyncExtension = SheetSyncExtension;
av.theExtensionManager.registerExtension(myExtensionName, SheetSyncExtension);
